import React from 'react';
import { Check, X, ArrowRight } from 'lucide-react';
function ImagePlaceholder({
  prompt,
  aspectRatio = 'video'
}: {
  prompt: string;
  aspectRatio?: 'video' | 'square' | 'wide';
}) {
  const aspectClass = aspectRatio === 'video' ? 'aspect-video' : aspectRatio === 'square' ? 'aspect-square' : 'aspect-[2/1]';
  return <div className={`bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg p-4 ${aspectClass} flex flex-col items-center justify-center my-6`} data-id="element-27">
      <p className="text-xs text-gray-400 uppercase tracking-wide mb-2" data-id="element-28">
        Image Placeholder
      </p>
      <p className="text-sm text-gray-600 text-center px-4 leading-relaxed" data-id="element-29">
        {prompt}
      </p>
    </div>;
}
function CTAButton() {
  return <div className="my-8 text-center" data-id="element-30">
      <a href="https://trynymera.com/products/nymera-lymphatic-drainage-softgels" className="inline-flex items-center justify-center gap-2 bg-[#0d5d55] hover:bg-[#0a4a44] text-white font-bold py-4 px-8 rounded-lg shadow-lg transform transition hover:scale-[1.02] text-lg" data-id="element-31">
        GET 30% OFF NYMERA NOW!
        <ArrowRight className="w-5 h-5" data-id="element-32" />
      </a>
      <p className="mt-3 text-sm text-gray-500" data-id="element-33">60-Day Money-Back Guarantee</p>
      <p className="text-xs text-gray-400 mt-1" data-id="element-34">
        ✓ Guaranteed Safe and Secure Checkout ✓ No-Hassle Returns ✓ Fast
        Shipping
      </p>
    </div>;
}
function IngredientCard({
  name,
  dose,
  description
}: {
  name: string;
  dose: string;
  description: string;
}) {
  return <div className="bg-[#21afa1]/10 border border-[#21afa1]/20 rounded-lg p-5 mb-4" data-id="element-35">
      <h4 className="font-bold text-gray-900 text-lg mb-2" data-id="element-36">
        {name} ({dose})
      </h4>
      <p className="text-gray-700" data-id="element-37">{description}</p>
    </div>;
}
function TestimonialCard({
  name,
  age,
  occupation,
  quote,
  highlight,
  image
}: {
  name: string;
  age: number;
  occupation: string;
  quote: string;
  highlight: string;
  image: string;
}) {
  return <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6" data-id="element-38">
      <div className="flex items-start gap-4 mb-4" data-id="element-39">
        <img src={image} alt={name} className="w-14 h-14 rounded-full object-cover border-2 border-gray-200 flex-shrink-0" data-id="element-40" />
        <div data-id="element-41">
          <h4 className="font-bold text-gray-900 text-lg mb-1" data-id="element-42">
            "{highlight}"
          </h4>
          <p className="text-sm text-gray-500" data-id="element-43">
            — {name}, {age}, {occupation}
          </p>
        </div>
      </div>
      <p className="text-gray-700 italic mb-3" data-id="element-44">"{quote}"</p>
      <p className="text-xs text-gray-400" data-id="element-45">Individual results may vary.</p>
    </div>;
}
function BulletList({
  items,
  type = 'check'
}: {
  items: string[];
  type?: 'check' | 'x' | 'neutral';
}) {
  return <ul className="space-y-2 my-4" data-id="element-46">
      {items.map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-47">
          {type === 'check' && <div className="mt-0.5 bg-[#21afa1] rounded-full p-0.5 flex-shrink-0" data-id="element-48">
              <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-49" />
            </div>}
          {type === 'x' && <div className="mt-0.5 bg-red-500 rounded-full p-0.5 flex-shrink-0" data-id="element-50">
              <X className="w-4 h-4 text-white" strokeWidth={3} data-id="element-51" />
            </div>}
          {type === 'neutral' && <span className="text-gray-400 mt-0.5" data-id="element-52">•</span>}
          <span className="text-gray-800" data-id="element-53">{item}</span>
        </li>)}
    </ul>;
}
export function ArticleContent() {
  return <div className="advertorial-body-text" data-id="element-54">
      {/* Introduction - The Problem (ADJUSTMENT #2) */}
      <section className="mb-10" data-id="element-55">
        <p className="mb-4" data-id="element-56">
          Hello, I'm Dr. Maria Castellanos, a{' '}
          <strong data-id="element-57">lymphatic health specialist</strong> based in Austin, Texas.
        </p>
        <p className="mb-4" data-id="element-58">
          For over 12 years, I've worked with women who've been told by doctors:
          "Just lose weight."
        </p>
        <p className="mb-4" data-id="element-59">
          Women whose upper bodies are thin—but whose legs won't respond no
          matter what they do.
        </p>
        <p className="mb-4" data-id="element-60">
          Women who can feel painful little nodules under their skin when they
          press on their thighs.
        </p>
        <p className="mb-4" data-id="element-61">
          Women who bruise from nothing. Whose legs feel fine at 8 AM but
          unbearably heavy by 3 PM.
        </p>
        <p className="mb-4" data-id="element-62">
          Women who've been dismissed, gaslit, and made to feel like they're
          making it up.
        </p>
        <p className="mb-4" data-id="element-63">
          <strong data-id="element-64">
            If this is you... you're not crazy. You're not imagining it. And
            it's not all in your head.
          </strong>
        </p>
        <p className="mb-4" data-id="element-65">
          You likely have <strong data-id="element-66">lipedema</strong>. And I'm about to explain
          what's actually happening inside your body—and why everything you've
          tried has failed.
        </p>
      </section>

      {/* The Pattern */}
      <section className="mb-10" data-id="element-67">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-68">
          If Your Pants Fit Perfectly at 8 AM But Feel Too Tight by 3 PM... Read
          This
        </h2>
        <p className="mb-4" data-id="element-69">
          <strong data-id="element-70">You're not crazy.</strong>
        </p>
        <p className="mb-4" data-id="element-71">
          <strong data-id="element-72">You're not imagining it.</strong>
        </p>
        <p className="mb-4" data-id="element-73">
          <strong data-id="element-74">And it's not all in your head.</strong>
        </p>
        <p className="mb-4" data-id="element-75">
          If your legs feel almost normal when you wake up, but by mid-afternoon
          they're heavy, achy, and swollen...
        </p>
        <p className="mb-4" data-id="element-76">
          If your legs look disproportionately larger than your upper body no
          matter how much you diet...
        </p>
        <p className="mb-4" data-id="element-77">
          If three doctors have told you to "just lose weight" even though you
          know it's not regular fat...
        </p>
        <p className="mb-4" data-id="element-78">Then you need to read this.</p>
        <p className="mb-4" data-id="element-79">
          Because what's happening inside your body — especially if you have
          lipedema — has nothing to do with willpower or calories.
        </p>
        <p className="mb-4 text-lg font-bold text-[#0d5d55]" data-id="element-80">
          It's happening at the vessel level.
        </p>
        <p className="mb-4" data-id="element-81">
          And once you understand why, relief finally makes sense.
        </p>

        {/* 8 AM vs 3 PM comparison image */}
        <div className="my-6 rounded-lg overflow-hidden" data-id="element-82">
          <img src="https://cdn.magicpatterns.com/uploads/tpRYn8HVuYbGjiWLpRqMPs/ChatGPT_Image_5_Φεβ_2026,_04_13_17_μ.μ..png" alt="Comparison showing legs at 8 AM vs 3 PM - morning vs afternoon swelling" className="w-full h-auto" data-id="element-83" />
        </div>
      </section>

      {/* Sarah's Story (ADJUSTMENT #3) */}
      <section className="mb-10" data-id="element-84">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-85">
          Meet Sarah: A 47-Year-Old Woman Who Thought She Was Losing Her Mind
        </h2>

        {/* Sarah at office image */}
        <div className="my-6 rounded-lg overflow-hidden" data-id="element-86">
          <img src="https://cdn.magicpatterns.com/uploads/aErf7eNkRtHCrYZtyymHxL/ChatGPT_Image_5_Φεβ_2026,_04_13_16_μ.μ..png" alt="Woman at office desk experiencing leg discomfort in the afternoon" className="w-full h-auto" data-id="element-87" />
        </div>

        <p className="mb-4" data-id="element-88">
          Sarah came to me after seeing three different doctors.
        </p>
        <p className="mb-4" data-id="element-89">
          All three said the same thing: "You need to lose weight."
        </p>
        <p className="mb-4 font-semibold" data-id="element-90">But here's what they didn't see:</p>
        <p className="mb-4" data-id="element-91">
          Sarah HAD been losing weight. Her face was thinner. Her arms were
          slimmer. Her stomach was flatter.
        </p>
        <p className="mb-4" data-id="element-92">But her legs? Exactly the same.</p>
        <p className="mb-4 font-bold" data-id="element-93">
          Her upper body was a size 6. Her legs were a size 14.
        </p>
        <p className="mb-4" data-id="element-94">
          And they weren't just fat. They were <strong data-id="element-95">PAINFUL</strong>.
        </p>
        <p className="mb-4" data-id="element-96">
          She could feel little nodules under her skin—like marbles trapped in
          tissue.
        </p>
        <p className="mb-4" data-id="element-97">
          She'd bruise from bumping into a table. Massive bruises that took
          weeks to heal.
        </p>
        <p className="mb-4 font-semibold" data-id="element-98">
          And every single day followed the same pattern:
        </p>
        <BulletList type="neutral" items={['Wake up: legs feel okay, ankles visible', 'By 10 AM: slight heaviness setting in', 'By 2 PM: legs feel full, achy', 'By 6 PM: thick columns of swollen tissue, no ankle definition']} data-id="element-99" />
        <p className="mb-4 italic" data-id="element-100">
          "I feel like I'm taking crazy pills," she told me during our first
          appointment.
        </p>
        <p className="mb-4 italic" data-id="element-101">
          "Everyone keeps saying it's just water retention. But I know my body.
          This isn't normal. Am I making this up as an excuse for my fat legs?"
        </p>
        <p className="mb-4" data-id="element-102">The self-doubt was crushing her.</p>

        <p className="mb-4 font-semibold" data-id="element-103">She'd tried everything:</p>
        <BulletList type="x" items={['Strict diets (1,200 calories/day for months)', 'Exercise 5 days/week (sometimes obsessively)', 'Compression garments (hot, uncomfortable, exhausting to wear)', 'Lymphatic massage ($150/session, twice a week)', 'Dry brushing, gua sha, elevation, cutting all salt']} data-id="element-104" />
        <p className="mb-4" data-id="element-105">
          <strong data-id="element-106">Nothing worked. Not for her legs.</strong>
        </p>
        <p className="mb-4" data-id="element-107">
          And the depression over her body was getting worse.
        </p>
        <p className="mb-4" data-id="element-108">This wasn't vanity. This was self-loathing.</p>
      </section>

      {/* The Discovery */}
      <section className="mb-10" data-id="element-109">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-110">
          Then I Asked Her a Question That Changed Everything
        </h2>
        <p className="mb-4" data-id="element-111">
          During one of our sessions, Sarah mentioned this morning-to-afternoon
          pattern for the third time.
        </p>
        <p className="mb-4" data-id="element-112">I stopped what I was doing.</p>
        <p className="mb-4 font-semibold" data-id="element-113">
          "Sarah, has anyone ever talked to you about your lymphatic vessels?"
        </p>
        <p className="mb-4" data-id="element-114">"You mean like... drainage?" she asked.</p>
        <p className="mb-4" data-id="element-115">
          "Not exactly," I said. "I mean the vessels themselves. The tiny pipes
          that are supposed to drain fluid."
        </p>
        <p className="mb-4" data-id="element-116">"What about them?"</p>
        <p className="mb-4 font-bold text-[#0d5d55]" data-id="element-117">
          "What if they're leaking?"
        </p>
        <p className="mb-4" data-id="element-118">
          I could see her face change. That one question opened up everything.
        </p>
        <p className="mb-4" data-id="element-119">
          You see, I'd been seeing this same pattern in patient after patient.
          And the traditional approach wasn't working.
        </p>
      </section>

      {/* The Science */}
      <section className="mb-10" data-id="element-120">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-121">
          The "Leaky Vessel" Discovery That Changed My Practice
        </h2>
        <p className="mb-4" data-id="element-122">
          After Sarah's appointment, I couldn't stop thinking about it.
        </p>
        <p className="mb-4" data-id="element-123">
          I spent that evening diving back into the research on lymphatic vessel
          integrity and inflammation.
        </p>
        <p className="mb-4 font-semibold" data-id="element-124">Here's what I found:</p>
        <p className="mb-4 text-lg font-bold text-[#0d5d55]" data-id="element-125">
          The real problem isn't water retention. It's the lymphatic vessels
          themselves.
        </p>

        {/* Leaky vessel diagram */}
        <div className="my-6 rounded-lg overflow-hidden bg-gray-50" data-id="element-126">
          <img src="https://cdn.magicpatterns.com/uploads/bCmSUf7SXnzbEYYXe2Tter/ChatGPT_Image_5_Φεβ_2026,_04_13_14_μ.μ..png" alt="Diagram comparing healthy vessel vs leaky vessel" className="w-full h-auto" data-id="element-127" />
        </div>

        <p className="mb-4" data-id="element-128">
          Let me explain it the way I explained it to Sarah.
        </p>
        <p className="mb-4" data-id="element-129">
          Think of your lymphatic system like tiny pipes that drain fluid from
          your tissues.
        </p>
        <p className="mb-4" data-id="element-130">
          When these pipes are strong and healthy, fluid moves through them
          smoothly. You don't even notice it happening.
        </p>
        <p className="mb-4" data-id="element-131">
          But when these pipes get weak or inflamed, they start to leak.
        </p>
        <p className="mb-4" data-id="element-132">
          Fluid seeps OUT of the vessels and gets trapped in your tissue.
        </p>
        <p className="mb-4" data-id="element-133">
          <strong data-id="element-134">That's when the swelling starts.</strong>
        </p>
        <p className="mb-4" data-id="element-135">
          <strong data-id="element-136">That's when your legs start feeling heavy.</strong>
        </p>
        <p className="mb-4" data-id="element-137">
          <strong data-id="element-138">That's when your pants stop fitting by afternoon.</strong>
        </p>
        <p className="mb-4 italic" data-id="element-139">
          I've been in practice for 12 years, and this was the missing piece I'd
          been looking for.
        </p>
      </section>

      {/* ADJUSTMENT #4: Lipedema Connection to Mechanism */}
      <section className="mb-10" data-id="element-140">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-141">
          But Here's What Makes Lipedema Even Worse
        </h2>
        <p className="mb-4" data-id="element-142">
          If you have lipedema, the fat tissue itself is chronically inflamed.
        </p>
        <p className="mb-4" data-id="element-143">
          It's not normal fat. It's inflamed, fibrotic fat releasing
          inflammatory signals 24/7.
        </p>
        <p className="mb-4 font-semibold" data-id="element-144">
          And those inflammatory signals actively DAMAGE your lymphatic vessel
          walls.
        </p>
        <p className="mb-4" data-id="element-145">So you're stuck in a vicious cycle:</p>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 my-6" data-id="element-146">
          <div className="space-y-2 text-gray-800" data-id="element-147">
            <p data-id="element-148">→ Lipedema fat releases inflammation</p>
            <p data-id="element-149">→ Inflammation damages vessel walls</p>
            <p data-id="element-150">→ Damaged vessels leak fluid</p>
            <p data-id="element-151">→ Fluid gets trapped in tissue</p>
            <p data-id="element-152">→ More swelling = more inflammation</p>
            <p className="font-bold" data-id="element-153">→ Cycle repeats and worsens over time</p>
          </div>
        </div>
        <p className="mb-4" data-id="element-154">
          <strong data-id="element-155">That's why the nodules form.</strong> That's why your legs
          feel so heavy. That's why you bruise so easily. That's why diet and
          exercise don't work on lipedema fat.
        </p>
        <p className="mb-4 font-bold text-[#0d5d55]" data-id="element-156">
          Your lymphatic vessels are being destroyed from the inside by the
          inflammation in your own fat tissue.
        </p>
        <p className="mb-4 font-semibold" data-id="element-157">
          And THIS is why everything you've tried has failed.
        </p>
      </section>

      {/* Why Morning vs Afternoon */}
      <section className="mb-10" data-id="element-158">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-159">
          Why You Feel Fine in the Morning But Miserable by 3 PM
        </h2>
        <p className="mb-4" data-id="element-160">
          This is the part that finally made everything click for Sarah.
        </p>
        <p className="mb-4" data-id="element-161">When you sleep, gravity is on your side.</p>
        <p className="mb-4" data-id="element-162">
          Your legs are elevated (or at least flat). Fluid can drain more
          easily.
        </p>
        <p className="mb-4" data-id="element-163">
          By morning, some of that trapped fluid has moved out. Your legs feel
          okay.
        </p>
        <p className="mb-4" data-id="element-164">
          But the second you stand up, gravity works against those weak vessels.
        </p>
        <p className="mb-4" data-id="element-165">
          Hour by hour, more fluid leaks out of those tiny pipes.
        </p>
        <p className="mb-4" data-id="element-166">It pools in your tissue.</p>
        <p className="mb-4" data-id="element-167">It builds up.</p>
        <BulletList type="neutral" items={['By 10 AM, you notice a little heaviness.', 'By 2 PM, your legs feel full and achy.', 'By 6 PM, they feel twice their normal size.']} data-id="element-168" />
        <p className="mb-4" data-id="element-169">
          It's not that you're "retaining water" from what you ate.
        </p>
        <p className="mb-4 font-bold" data-id="element-170">
          It's that your vessels are leaking all day long.
        </p>
        <p className="mb-4" data-id="element-171">Think of it like a garden hose with tiny holes.</p>
        <p className="mb-4" data-id="element-172">The water drips out slowly, steadily, all day.</p>
        <p className="mb-4" data-id="element-173">
          By evening, your whole garden is flooded even though the hose is still
          technically "working."
        </p>
        <p className="mb-4 font-semibold" data-id="element-174">
          That's what's happening inside your legs.
        </p>
      </section>

      {/* Causes */}
      <section className="mb-10" data-id="element-175">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-176">
          What Causes Vessels to Get Weak and Leaky?
        </h2>
        <p className="mb-4" data-id="element-177">Three main things:</p>
        <div className="space-y-4 mb-6" data-id="element-178">
          <div className="bg-gray-50 p-4 rounded-lg" data-id="element-179">
            <h4 className="font-bold text-gray-900" data-id="element-180">1. Hormones</h4>
            <p className="text-gray-700" data-id="element-181">
              When hormone levels shift (especially during perimenopause and
              menopause), vessel walls can get weaker.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg" data-id="element-182">
            <h4 className="font-bold text-gray-900" data-id="element-183">2. Inflammation</h4>
            <p className="text-gray-700" data-id="element-184">
              Chronic low-grade inflammation damages the vessel lining. Over
              time, those walls break down.
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg" data-id="element-185">
            <h4 className="font-bold text-gray-900" data-id="element-186">3. Genetics</h4>
            <p className="text-gray-700" data-id="element-187">
              Some women are born with vessels that are more prone to this
              problem. It runs in families.
            </p>
          </div>
        </div>
        <p className="mb-4 font-semibold" data-id="element-188">
          Here's the key insight that changed everything for Sarah:
        </p>
        <p className="mb-4" data-id="element-189">This is why dieting doesn't work.</p>
        <p className="mb-4" data-id="element-190">You can't diet away leaky vessels.</p>
        <p className="mb-4" data-id="element-191">This is why compression only helps temporarily.</p>
        <p className="mb-4" data-id="element-192">
          You're squeezing the fluid around, but you're not fixing the leak.
        </p>
        <p className="mb-4" data-id="element-193">This is why massage feels good but doesn't last.</p>
        <p className="mb-4" data-id="element-194">
          You're moving fluid through the system, but the vessels are still
          weak.
        </p>
        <p className="mb-4 text-lg font-bold text-[#0d5d55]" data-id="element-195">
          You need to strengthen and seal those vessel walls.
        </p>
      </section>

      {/* Why Everything Failed */}
      <section className="mb-10" data-id="element-196">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-197">
          Why Everything You've Tried Has Failed
        </h2>
        <p className="mb-4" data-id="element-198">Let's be honest about what you've already done.</p>

        {/* Failed treatments flat lay */}
        <div className="my-6 rounded-lg overflow-hidden" data-id="element-199">
          <img src="https://cdn.magicpatterns.com/uploads/1quUrVvNw1qyP4M19RQvgZ/ChatGPT_Image_5_Φεβ_2026,_04_13_13_μ.μ..png" alt="Failed treatments - compression socks, supplements, massage therapy, diet book, water bottle with X marks" className="w-full h-auto" data-id="element-200" />
        </div>

        <div className="space-y-6 mb-6" data-id="element-201">
          <div data-id="element-202">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-203">
              Compression Garments
            </h4>
            <p className="text-gray-700 mb-2" data-id="element-204">
              Yes, they squeeze fluid temporarily. But they don't fix the
              vessels.
            </p>
            <p className="text-gray-700" data-id="element-205">
              They're hot. Uncomfortable. Exhausting to wear.
            </p>
            <p className="text-gray-600 italic text-sm mt-2" data-id="element-206">
              As one woman in a lipedema support group put it: "Not worth the
              hours per day it takes."
            </p>
          </div>

          <div data-id="element-207">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-208">
              Lymphatic Massage (MLD)
            </h4>
            <p className="text-gray-700 mb-2" data-id="element-209">
              It moves fluid around, which feels amazing. But your vessels are
              still leaking.
            </p>
            <p className="text-gray-700" data-id="element-210">
              So within hours, you're right back where you started.
            </p>
            <p className="text-gray-700" data-id="element-211">
              Plus, it's expensive. Time-consuming. You need appointments every
              week.
            </p>
          </div>

          <div data-id="element-212">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-213">
              Generic Supplements
            </h4>
            <p className="text-gray-700 mb-2" data-id="element-214">
              Most supplements target inflammation (which is good). But they
              completely miss the vessel integrity piece.
            </p>
            <p className="text-gray-700" data-id="element-215">
              They're "kitchen sink" formulas with a little bit of everything
              and not enough of anything.
            </p>
          </div>

          <div data-id="element-216">
            <h4 className="font-bold text-gray-900 mb-2" data-id="element-217">Diet and Exercise</h4>
            <p className="text-gray-700 mb-2" data-id="element-218">
              You've already done everything right. You've cut carbs. You've
              tried keto. You've walked every day.
            </p>
            <p className="text-gray-700" data-id="element-219">
              But lipedema fat doesn't respond like regular fat. And weak
              vessels don't get stronger from willpower.
            </p>
          </div>
        </div>
      </section>

      {/* ADJUSTMENT #5: Betrayal/Rage Section */}
      <section className="mb-10" data-id="element-220">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-221">
          And Here's What Makes Me Angry as a Clinician
        </h2>
        <p className="mb-4" data-id="element-222">
          I see women spending THOUSANDS of dollars on treatments that can't
          possibly work.
        </p>
        <p className="mb-4" data-id="element-223">Not because the treatments are scams.</p>
        <p className="mb-4" data-id="element-224">
          But because they're addressing the <strong data-id="element-225">WRONG PROBLEM</strong>.
        </p>
        <p className="mb-4" data-id="element-226">
          Compression? It squeezes fluid around. But it doesn't seal the leaky
          vessels.
        </p>
        <p className="mb-4" data-id="element-227">
          Massage? It moves fluid temporarily. But your vessels are still
          damaged.
        </p>
        <p className="mb-4" data-id="element-228">
          Diet? You can't diet away vessel inflammation caused by lipedema fat.
        </p>
        <p className="mb-4 font-semibold" data-id="element-229">
          And yet, this is what doctors keep telling you to do.
        </p>
        <p className="mb-4 italic" data-id="element-230">
          "Just lose weight." "Try keto." "Wear compression every day." "Book
          massage twice a week."
        </p>
        <p className="mb-4" data-id="element-231">
          They're treating the symptoms while the vessel damage compounds
          underneath.
        </p>
        <p className="mb-4" data-id="element-232">Not because they're bad doctors.</p>
        <p className="mb-4 font-bold text-[#0d5d55]" data-id="element-233">
          But because vessel integrity ISN'T TAUGHT in most medical training.
        </p>
        <p className="mb-4" data-id="element-234">They don't know what they don't know.</p>
        <p className="mb-4" data-id="element-235">
          And you're the one paying the price—financially, physically,
          emotionally.
        </p>
        <p className="mb-4 font-bold text-lg" data-id="element-236">That stops today.</p>
      </section>

      {/* The Missing Piece */}
      <section className="mb-10" data-id="element-237">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-238">
          The Missing Piece Nobody Was Talking About
        </h2>
        <p className="mb-4" data-id="element-239">
          For years, doctors and therapists focused on moving fluid OUT of the
          tissue.
        </p>
        <p className="mb-4" data-id="element-240">Compression. Massage. Elevation. Diuretics.</p>
        <p className="mb-4" data-id="element-241">
          But nobody was addressing the vessel walls themselves.
        </p>
        <p className="mb-4" data-id="element-242">Nobody was asking: "How do we stop the leak?"</p>
        <p className="mb-4" data-id="element-243">
          Until researchers studying lymphatic health found something
          remarkable.
        </p>
      </section>

      {/* The Solution */}
      <section className="mb-10" data-id="element-244">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-245">
          Why I Started Recommending a Specific 6-Botanical Formula
        </h2>
        <p className="mb-4" data-id="element-246">
          After understanding the leaky vessel mechanism, I started researching
          botanical compounds known for supporting vascular health.
        </p>
        <p className="mb-4" data-id="element-247">
          I wanted to know: Could certain plant extracts actually strengthen
          vessel walls?
        </p>
        <p className="mb-4 font-semibold" data-id="element-248">The answer was yes.</p>
        <p className="mb-4" data-id="element-249">
          Specific botanicals — when used at clinical doses — could:
        </p>
        <BulletList type="check" items={['Help seal leaky vessel walls', 'Reduce the inflammation that weakens them', 'Support healthy fluid movement through the lymphatic system']} data-id="element-250" />
        <p className="mb-4" data-id="element-251">Not all at once. Not overnight.</p>
        <p className="mb-4" data-id="element-252">But consistently, over weeks of daily use.</p>
        <p className="mb-4 font-semibold" data-id="element-253">
          That's when I discovered Nymera Lymphatic Drainage Softgels.
        </p>
        <p className="mb-4" data-id="element-254">
          I'll be honest — I was skeptical at first. I've seen too many
          "miracle" supplements that promise everything and deliver nothing.
        </p>
        <p className="mb-4" data-id="element-255">
          But when I looked at the ingredient list and the dosages, something
          clicked.
        </p>
        <p className="mb-4" data-id="element-256">This wasn't another kitchen sink formula.</p>
        <p className="mb-4 font-bold" data-id="element-257">
          It was six specific botanicals, each chosen for a reason. Each at a
          dose that matched the research.
        </p>
      </section>

      {/* Ingredients */}
      <section className="mb-10" data-id="element-258">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-259">
          The 6 Key Ingredients
        </h2>

        {/* 6 Botanicals flat lay */}
        <div className="my-6 rounded-lg overflow-hidden" data-id="element-260">
          <img src="https://cdn.magicpatterns.com/uploads/g2NRuuvoscoM8FUQYwG7cv/ChatGPT_Image_5_Φεβ_2026,_04_13_12_μ.μ..png" alt="Six botanical ingredients - echinacea, dandelion, burdock root, calendula, red clover, and nettle" className="w-full h-auto" data-id="element-261" />
        </div>

        <IngredientCard name="Ingredient #1: Echinacea" dose="1000mg" description="Helps calm inflammatory signaling that weakens vessel walls. Studies show it can lower pro-inflammatory cytokines like IL-6 and TNF. Translation? Less inflammation = stronger vessels." data-id="element-262" />

        <IngredientCard name="Ingredient #2: Dandelion" dose="700mg" description="Supports healthy fluid movement. A human study found that dandelion extract increased urinary frequency over a single day. It helps your body flush what's stuck." data-id="element-263" />

        <IngredientCard name="Ingredient #3: Burdock" dose="600mg" description="Targets the specific inflammatory markers (IL-6, hs-CRP) linked to vessel damage. A clinical trial in humans showed it significantly reduced these markers. Less inflammation. Less stress on your vessels." data-id="element-264" />

        <IngredientCard name="Ingredient #4: Calendula" dose="400mg" description="Protects tissue from oxidative stress. A randomized controlled human study showed it reduced inflammatory tissue irritation. Think of it as a shield for your vessel walls." data-id="element-265" />

        <IngredientCard name="Ingredient #5: Red Clover" dose="400mg" description="Improves vascular function and lowers vascular resistance. Two human trials found it helped blood vessels work better and reduced stiffness. Better vessel function = better drainage." data-id="element-266" />

        <IngredientCard name="Ingredient #6: Nettle" dose="300mg" description="Modulates inflammatory signaling tied to swelling and tenderness. A double-blind, placebo-controlled trial showed it affected immune and inflammatory markers. It's the final piece of the anti-inflammation puzzle." data-id="element-267" />
      </section>

      {/* Why Softgels */}
      <section className="mb-10" data-id="element-268">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-269">
          Why Softgels Matter (And Why Most Supplements Fail)
        </h2>
        <p className="mb-4" data-id="element-270">Here's something most people don't know:</p>
        <p className="mb-4" data-id="element-271">
          Many of these botanicals are oils or fat-soluble compounds.
        </p>
        <p className="mb-4" data-id="element-272">
          If you take them in powder or capsule form, your body struggles to
          absorb them.
        </p>
        <p className="mb-4 font-semibold" data-id="element-273">
          Softgels protect these compounds and deliver them in a form your body
          can actually use.
        </p>

        {/* Softgels image */}
        <div className="my-6 rounded-lg overflow-hidden" data-id="element-274">
          <img src="https://cdn.magicpatterns.com/uploads/1YgfAoPkyQJg6QgCVCombU/ChatGPT_Image_5_Φεβ_2026,_04_13_11_μ.μ..png" alt="Golden softgel capsules spilling from a teal bottle with botanical leaves" className="w-full h-auto" data-id="element-275" />
        </div>

        <p className="mb-4" data-id="element-276">
          Plus, they're easier on your stomach. No chalky taste. No digestive
          upset.
        </p>
        <p className="mb-4 font-bold" data-id="element-277">
          Just two softgels in the morning with breakfast. That's it.
        </p>
      </section>

      {/* What to Expect */}
      <section className="mb-10" data-id="element-278">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-279">
          What to Expect When You Start Taking Nymera
        </h2>
        <p className="mb-4" data-id="element-280">
          Let's be realistic here. This isn't a magic cure.
        </p>
        <p className="mb-4" data-id="element-281">
          You're not going to wake up tomorrow with perfect legs.
        </p>
        <p className="mb-4 font-semibold" data-id="element-282">
          What you ARE doing is rebuilding vessel strength over time.
        </p>
        <p className="mb-4" data-id="element-283">Here's what most women notice:</p>

        <div className="space-y-6 mb-6" data-id="element-284">
          <div className="border-l-4 border-[#21afa1] pl-4" data-id="element-285">
            <h4 className="font-bold text-gray-900" data-id="element-286">Week 1</h4>
            <p className="text-gray-700" data-id="element-287">
              Many report their legs feel less "full" by evening. The achy
              feeling during flare-ups starts to ease. It's subtle at first, but
              it's there.
            </p>
          </div>

          <div className="border-l-4 border-[#21afa1] pl-4" data-id="element-288">
            <h4 className="font-bold text-gray-900" data-id="element-289">Week 2-3</h4>
            <p className="text-gray-700" data-id="element-290">
              The progressive worsening throughout the day becomes less
              dramatic. Instead of feeling fine at 8 AM and miserable by 2 PM...
              You might feel okay until 4 or 5 PM. Your pants still fit by
              afternoon.
            </p>
          </div>

          <div className="border-l-4 border-[#21afa1] pl-4" data-id="element-291">
            <h4 className="font-bold text-gray-900" data-id="element-292">Week 4-6</h4>
            <p className="text-gray-700" data-id="element-293">
              The heaviness that used to hit by 3 PM shows up later. Or not at
              all. You realize you haven't needed compression as much. You have
              more energy because you're not fighting your body all day.
            </p>
          </div>

          <div className="border-l-4 border-[#21afa1] pl-4" data-id="element-294">
            <h4 className="font-bold text-gray-900" data-id="element-295">Ongoing</h4>
            <p className="text-gray-700" data-id="element-296">
              With continued use, vessel integrity keeps improving. Inflammation
              stays lower. You're addressing the root cause, not just masking
              symptoms.
            </p>
          </div>
        </div>

        <p className="mb-4 font-semibold" data-id="element-297">
          Most women notice the difference within the first few weeks.
        </p>
        <p className="mb-4" data-id="element-298">And it only takes 2 softgels per day.</p>
      </section>

      <CTAButton data-id="element-299" />

      {/* I Started Recommending It */}
      <section className="mb-10" data-id="element-300">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-301">
          I Started Recommending It to My Patients (And Tracking the Results)
        </h2>
        <p className="mb-4" data-id="element-302">
          I started suggesting Nymera to patients who fit the profile:
        </p>
        <p className="mb-4" data-id="element-303">
          Women with the progressive afternoon worsening pattern. Women who'd
          tried everything else. Women tired of spending thousands on temporary
          fixes.
        </p>
        <p className="mb-4" data-id="element-304">
          Within three months, I had results I couldn't ignore.
        </p>
        <p className="mb-4" data-id="element-305">
          Patient after patient came back reporting the same things:
        </p>
        <p className="mb-4 italic" data-id="element-306">
          "My legs don't feel as heavy by afternoon anymore."
        </p>
        <p className="mb-4 italic" data-id="element-307">
          "I noticed I can go longer without needing compression."
        </p>
        <p className="mb-4 italic" data-id="element-308">
          "The achy feeling during flare-ups isn't as bad."
        </p>
        <p className="mb-4" data-id="element-309">
          These weren't dramatic overnight transformations.
        </p>
        <p className="mb-4" data-id="element-310">
          But they were consistent, measurable improvements.
        </p>
        <p className="mb-4" data-id="element-311">The kind that actually matter in daily life.</p>
      </section>

      {/* Testimonials */}
      <section className="mb-10" data-id="element-312">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-313">
          Real Women, Real Results
        </h2>
        <p className="mb-6" data-id="element-314">
          Let me share what some of my patients have told me. (I've changed
          names for privacy, but these are real results from real women.)
        </p>

        <TestimonialCard name="Jennifer M." age={52} occupation="Teacher" highlight="Three Doctors Told Me to 'Just Lose Weight.' Dr. Castellanos Was the First Person Who Believed Me." quote="I'd lost 35 pounds. My arms were thin. My stomach was flat. But my legs? Still huge and painful. My doctor looked at me and said: 'Well, some people just carry weight in their legs. Keep trying.' I wanted to scream. When I found Dr. Castellanos, she looked at my legs and said: 'You have lipedema. And everything you've been told to do is wrong for this condition.' I cried. Finally, someone believed me. She explained the leaky vessel mechanism and recommended Nymera. Within three weeks, the afternoon heaviness was 60% better. Six months later, I can stand through my entire teaching day without that awful weight in my legs. I wish I'd found her—and Nymera—five years ago." image="https://images.unsplash.com/photo-1598550874175-4d0ef436c909?ixlib=rb-4.0.3&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" data-id="element-315" />

        <TestimonialCard name="Rebecca T." age={48} occupation="Nurse" highlight="I Stopped Fearing Flare-Ups" quote="The achy feeling I get during flare-ups used to completely derail my week. I'd have to call in sick. I couldn't work. I started taking Nymera hoping for anything — even just 10% relief would have been worth it. Within two weeks, I had a flare-up. I braced myself for the usual misery. But it was... manageable. Still uncomfortable, but not unbearable. Now I feel like I have my body back. I'm not scared of the next flare-up because I know I can handle it." image="https://cdn.magicpatterns.com/uploads/hBnsjD7vKHqhHSHxUbALDV/istockphoto-1437816897-612x612_(1).jpg" data-id="element-316" />

        <TestimonialCard name="Lisa K." age={55} occupation="Marketing Director" highlight="Finally, Something That Makes Sense" quote="I've tried EVERYTHING. Diets, exercise, compression, massage, you name it. My supplement list used to look insane. Fifteen different bottles. I couldn't even remember what half of them were for. Nymera is the first thing that actually addressed WHY my legs swell instead of just trying to push fluid around. I'm down to just these softgels and my usual vitamins. And my legs feel better than they have in years. I wish I'd found this five years ago." image="https://images.unsplash.com/photo-1567532939604-b6b5b0db2604?ixlib=rb-4.0.3&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" data-id="element-317" />

        <TestimonialCard name="Patricia L." age={59} occupation="Retail Manager" highlight="My Compression Socks Are Sitting in My Drawer" quote="I used to wear compression every single day. It was non-negotiable. If I didn't wear them, my legs would be unbearable by closing time. After six weeks on Nymera, I realized I'd forgotten to put them on one morning. And you know what? I made it through my whole shift without needing them. Now I only wear compression maybe once a week on really long days. That's life-changing for someone who felt trapped in those hot, uncomfortable things." image="https://images.unsplash.com/photo-1593104547489-5cfb3839a3b5?ixlib=rb-4.0.3&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" data-id="element-318" />

        <TestimonialCard name="Michelle D." age={51} occupation="Accountant" highlight="I Can Walk My Dog Again" quote="I love my dog, but I'd stopped walking her because by the time I got home from work, my legs were too heavy and sore. My husband started doing all the walks. I felt guilty every time. Three weeks into Nymera, I grabbed the leash one evening without thinking about it. We walked for 20 minutes. My legs were tired but not that awful, achy heavy feeling. Now we walk together every night. It sounds small, but it gave me back something I really missed." image="https://images.unsplash.com/photo-1548142813-c348350df52b?ixlib=rb-4.0.3&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" data-id="element-319" />

        <TestimonialCard name="Karen M." age={44} occupation="Office Administrator" highlight="I Don't Dread Afternoons Anymore" quote="Every day around 2 PM, I'd start watching the clock. Counting down the hours until I could go home and elevate my legs. It was depressing. Like my whole life revolved around managing my legs. After a month on Nymera, I noticed I wasn't watching the clock anymore. My legs still aren't perfect, but they're manageable. I can focus on my work instead of my pain. That mental shift alone was worth it." image="https://images.unsplash.com/photo-1601288496920-b6154fe3626a?ixlib=rb-4.0.3&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" data-id="element-320" />

        <TestimonialCard name="Susan R." age={56} occupation="Real Estate Agent" highlight="My Daughter Asked If I'd Had Surgery" quote="I haven't seen my daughter in person for about four months (she lives out of state). When she visited last month, she looked at my legs and said, 'Mom, did you have surgery? They look so much less swollen.' I hadn't. Just Nymera for three months. Even she could see the difference. What she doesn't know yet is that her legs are starting to look like mine did at her age. She's only 28. I'm terrified for her. But at least now I know there's something that might actually help when she's ready." image="https://images.unsplash.com/photo-1566616213894-2d4e1baee5d8?ixlib=rb-4.0.3&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" data-id="element-321" />
      </section>

      {/* Why I'm Sharing This Publicly */}
      <section className="mb-10" data-id="element-322">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-323">
          Why I'm Sharing This Publicly
        </h2>
        <p className="mb-4" data-id="element-324">Look, I'm a clinician. Not a marketer.</p>
        <p className="mb-4" data-id="element-325">I don't get paid to write articles like this.</p>
        <p className="mb-4" data-id="element-326">
          But after seeing these results in my own practice — after watching
          women get their lives back — I felt I had to say something.
        </p>
        <p className="mb-4" data-id="element-327">
          If you're in any lipedema Facebook groups or Reddit communities, you
          might have already seen women mentioning Nymera.
        </p>
        <p className="mb-4" data-id="element-328">Not because it's a miracle cure.</p>
        <p className="mb-4" data-id="element-329">Not because it "fixes" lipedema overnight.</p>
        <p className="mb-4" data-id="element-330">
          But because it finally addresses the vessel problem that other
          solutions ignore.
        </p>
        <p className="mb-4" data-id="element-331">One woman wrote:</p>
        <blockquote className="border-l-4 border-[#21afa1] pl-4 italic text-gray-700 mb-4" data-id="element-332">
          "I was skeptical because I've been burned so many times. But after
          reading about the mechanism — the leaky vessels thing — it actually
          made sense. I figured I'd try it for a month. That was six months ago.
          I'm not stopping."
        </blockquote>
        <p className="mb-4" data-id="element-333">Another said:</p>
        <blockquote className="border-l-4 border-[#21afa1] pl-4 italic text-gray-700 mb-4" data-id="element-334">
          "My physical therapist asked what I'd been doing differently because
          my tissue felt less congested. When I told her about Nymera, she wrote
          down the name for three other patients."
        </blockquote>
        <p className="mb-4" data-id="element-335">This isn't hype.</p>
        <p className="mb-4" data-id="element-336">
          It's women who are tired of wasting money on things that don't work.
        </p>
      </section>

      {/* What This Is and Isn't */}
      <section className="mb-10" data-id="element-337">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-338">
          Let's Be Clear About What This Isn't
        </h2>
        <BulletList type="x" items={["Nymera is NOT a weight loss supplement. It won't make you drop dress sizes.", 'Nymera is NOT a "cure" for lipedema. Lipedema is a chronic condition. This won\'t make it disappear.', 'Nymera is NOT a replacement for medical care. You should still see your doctors. Still work with your physical therapist.']} data-id="element-339" />

        <h3 className="font-bold text-xl text-gray-900 mt-8 mb-4" data-id="element-340">
          Here's What Nymera IS:
        </h3>
        <BulletList type="check" items={['A botanical stack that supports vessel integrity. It helps seal those leaky walls so fluid stays where it belongs.', 'A way to reduce inflammation at the vessel level. Less inflammation = less damage = stronger vessels.', 'A tool that supports healthy lymphatic flow. Better flow = less pooling = less heaviness.']} data-id="element-341" />
        <p className="mb-4" data-id="element-342">
          It works alongside your other care. Not instead of it.
        </p>
        <p className="mb-4" data-id="element-343">
          You might still need compression some days. You might still benefit
          from massage.
        </p>
        <p className="mb-4 font-semibold" data-id="element-344">
          But many women find they need these things less often.
        </p>
      </section>

      {/* Safety */}
      <section className="mb-10 bg-yellow-50 border border-yellow-200 rounded-lg p-6" data-id="element-345">
        <h2 className="font-bold text-xl text-gray-900 mb-4" data-id="element-346">
          Important Safety Notes
        </h2>
        <p className="mb-4" data-id="element-347">
          Always check with your doctor before starting any new supplement.
        </p>
        <p className="mb-4" data-id="element-348">This is especially important if:</p>
        <BulletList type="neutral" items={["You're on blood thinners (one of the ingredients can affect clotting)", "You're pregnant or nursing", 'You have any medical conditions', "You're taking other medications"]} data-id="element-349" />
        <p className="mb-4" data-id="element-350">
          Nymera is made in the USA. It's third-party tested for purity and
          potency.
        </p>
        <p className="font-semibold" data-id="element-351">
          But you know your body best. And your doctor knows your medical
          history. Please don't skip this step.
        </p>
      </section>

      {/* Sarah's Result */}
      <section className="mb-10" data-id="element-352">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-353">
          Sarah's Decision (And What Happened Next)
        </h2>
        <p className="mb-4" data-id="element-354">
          Remember Sarah from the beginning of this article?
        </p>
        <p className="mb-4" data-id="element-355">
          After learning about the leaky vessel mechanism, she decided to try
          Nymera.
        </p>
        <p className="mb-4" data-id="element-356">
          She figured after wasting thousands of dollars on treatments that
          didn't work... What's one more month?
        </p>
        <p className="mb-4" data-id="element-357">Especially with a 60-day money-back guarantee.</p>
        <p className="mb-4 font-semibold" data-id="element-358">Here's what happened:</p>
        <p className="mb-4" data-id="element-359">
          Within 10 days, she noticed less heaviness by mid-afternoon. It wasn't
          dramatic. But it was there.
        </p>
        <p className="mb-4" data-id="element-360">
          By week three, she realized she hadn't worn compression in four days.
          She just... forgot. Because she didn't need it.
        </p>

        {/* Sarah happy walking outdoors */}
        <div className="my-6 rounded-lg overflow-hidden" data-id="element-361">
          <img src="https://cdn.magicpatterns.com/uploads/dsBAJZkSXKrimanvoz6msk/ChatGPT_Image_5_Φεβ_2026,_04_15_38_μ.μ..png" alt="Happy middle-aged woman walking outdoors on a nature trail at golden hour" className="w-full h-auto" data-id="element-362" />
        </div>

        <p className="mb-4 font-semibold" data-id="element-363">Six months later, Sarah says:</p>
        <p className="mb-4 italic text-lg" data-id="element-364">
          "I finally feel like I'm not fighting my body every single day. My
          legs still aren't 'perfect.' I haven't found anything that helps with
          appearance yet. But the daily pain and heaviness? Gone. I can live my
          life again. That's everything."
        </p>
        <p className="text-sm text-gray-500" data-id="element-365">Individual results may vary.</p>
      </section>

      {/* ADJUSTMENT #7: Stronger CTA Section */}
      <section className="mb-10" data-id="element-366">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-367">
          You've Already Wasted Enough Money on Treatments That Don't Work
        </h2>
        <p className="mb-4" data-id="element-368">Let me be direct with you.</p>
        <p className="mb-4" data-id="element-369">
          If you have lipedema, compression and massage are NOT going to fix
          your vessels.
        </p>
        <p className="mb-4" data-id="element-370">They can't.</p>
        <p className="mb-4" data-id="element-371">
          They're external treatments trying to solve an internal problem.
        </p>
        <p className="mb-4 font-semibold" data-id="element-372">
          You've already spent hundreds—maybe thousands—on:
        </p>
        <BulletList type="x" items={["Compression garments that only help while you're wearing them", 'Massage sessions that feel good for 12 hours and then stop working', 'Supplements that target inflammation but miss the vessel integrity piece', 'Diets that work everywhere except your legs']} data-id="element-373" />
        <p className="mb-4 font-bold" data-id="element-374">
          How much more are you willing to spend on temporary fixes?
        </p>
        <p className="mb-4" data-id="element-375">
          Nymera addresses the <strong data-id="element-376">ROOT PROBLEM</strong>: damaged, leaky
          lymphatic vessels.
        </p>
        <p className="mb-4" data-id="element-377">
          For less than the cost of ONE massage session, you get a full month
          supply.
        </p>
        <p className="mb-4" data-id="element-378">With a 60-day money-back guarantee.</p>
        <p className="mb-4" data-id="element-379">
          If your legs don't feel less heavy by afternoon...
        </p>
        <p className="mb-4" data-id="element-380">If the aching doesn't improve...</p>
        <p className="mb-4" data-id="element-381">If you don't notice a difference...</p>
        <p className="mb-4 font-bold text-lg" data-id="element-382">
          Full refund. No questions asked.
        </p>
        <p className="mb-4" data-id="element-383">You've already been dismissed by enough people.</p>
        <p className="mb-6 font-bold text-[#0d5d55]" data-id="element-384">
          We're not adding to that list.
        </p>

        <div className="flex flex-col items-center gap-4" data-id="element-385">
          <div className="text-2xl animate-bounce" data-id="element-386">👇</div>
          <a href="https://trynymera.com/products/nymera-lymphatic-drainage-softgels" className="inline-flex items-center justify-center gap-2 bg-[#0d5d55] hover:bg-[#0a4a44] text-white font-bold py-4 px-8 rounded-lg shadow-lg transform transition hover:scale-[1.02] text-lg w-full md:w-auto" data-id="element-387">
            CLAIM YOUR 30% OFF NOW
            <ArrowRight className="w-5 h-5" data-id="element-388" />
          </a>
          <div className="text-2xl animate-bounce" data-id="element-389">👇</div>
        </div>

        <div className="mt-8 text-center" data-id="element-390">
          <p className="text-sm text-gray-500" data-id="element-391">60-Day Money-Back Guarantee</p>
          <p className="text-xs text-gray-400 mt-1" data-id="element-392">
            ✓ Guaranteed Safe and Secure Checkout ✓ No-Hassle Returns ✓ Fast
            Shipping
          </p>
        </div>
      </section>

      {/* Urgency */}
      <section className="mb-10" data-id="element-393">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-394">
          Why Inventory Has Been Tight Lately
        </h2>
        <p className="mb-4" data-id="element-395">Here's the honest truth:</p>
        <p className="mb-4" data-id="element-396">
          Since women in lipedema support groups started sharing their results,
          inventory has been inconsistent.
        </p>
        <p className="mb-4" data-id="element-397">
          Nymera is a small company. They can't produce massive quantities
          overnight.
        </p>
        <p className="mb-4" data-id="element-398">
          Every batch is third-party tested. Every ingredient is verified for
          potency. That takes time.
        </p>
        <p className="mb-4" data-id="element-399">I'm not trying to create fake urgency here.</p>
        <p className="mb-4 font-semibold" data-id="element-400">
          But if you're going to try this, now is the time.
        </p>
        <p className="mb-4" data-id="element-401">
          While the 30% discount is active and inventory is available.
        </p>
      </section>

      {/* Two Choices */}
      <section className="mb-10" data-id="element-402">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-403">
          You Have Two Choices Right Now
        </h2>

        <div className="grid md:grid-cols-2 gap-6 mb-8" data-id="element-404">
          <div className="bg-gray-100 rounded-lg p-6" data-id="element-405">
            <h3 className="font-bold text-lg text-gray-900 mb-4" data-id="element-406">
              Choice 1: Keep Doing What You're Doing
            </h3>
            <p className="text-gray-700 mb-4" data-id="element-407">
              Keep watching your legs get heavier each afternoon.
            </p>
            <p className="text-gray-700 mb-4" data-id="element-408">
              Keep spending money on compression, massage, and supplements that
              don't address the root problem.
            </p>
            <p className="text-gray-700 mb-4" data-id="element-409">
              Keep hoping something will eventually work.
            </p>
            <p className="text-gray-700" data-id="element-410">
              That's a valid choice. But you already know where that path leads.
            </p>
          </div>

          <div className="bg-[#21afa1]/10 border-2 border-[#21afa1] rounded-lg p-6" data-id="element-411">
            <h3 className="font-bold text-lg text-gray-900 mb-4" data-id="element-412">
              Choice 2: Try Addressing the Vessel Problem for 60 Days
            </h3>
            <p className="text-gray-700 mb-4" data-id="element-413">
              See if strengthening those leaky walls makes a difference.
            </p>
            <p className="text-gray-700 mb-4" data-id="element-414">
              See if reducing inflammation at the vessel level changes your
              daily pattern.
            </p>
            <p className="text-gray-700 mb-4" data-id="element-415">
              Risk-free with the 60-day guarantee.
            </p>
            <p className="text-gray-700 font-semibold" data-id="element-416">
              If it doesn't work, you get your money back.
            </p>
            <p className="text-gray-700 font-semibold" data-id="element-417">
              If it does work... you get your afternoons back.
            </p>
          </div>
        </div>
      </section>

      {/* Final Push */}
      <section className="mb-10 text-center" data-id="element-418">
        <h2 className="advertorial-headline text-2xl md:text-3xl text-gray-900 mb-5" data-id="element-419">
          Remember How You Felt This Morning?
        </h2>
        <p className="mb-4" data-id="element-420">When you first woke up and your legs were okay?</p>
        <p className="mb-4" data-id="element-421">
          When you thought, "Maybe today will be different"?
        </p>
        <p className="mb-4 font-semibold" data-id="element-422">
          Imagine if they still felt that way at 3 PM. At 6 PM. At bedtime.
        </p>
        <p className="mb-4" data-id="element-423">
          Imagine going through your whole day without that awful heaviness
          setting in.
        </p>
        <p className="mb-4" data-id="element-424">
          Without counting down the hours until you can elevate your legs.
        </p>
        <p className="mb-4" data-id="element-425">
          Without avoiding activities because you know your legs can't handle
          it.
        </p>
        <p className="mb-6 font-bold text-lg text-[#0d5d55]" data-id="element-426">
          That's what addressing the leak could mean.
        </p>

        <h3 className="font-bold text-xl text-gray-900 mb-4" data-id="element-427">
          Click Below to Claim Your 30% Off
        </h3>
        <p className="mb-4" data-id="element-428">You've read this far.</p>
        <p className="mb-4" data-id="element-429">You know the mechanism makes sense.</p>
        <p className="mb-4" data-id="element-430">You know the ingredients are backed by research.</p>
        <p className="mb-4" data-id="element-431">You know there's a guarantee protecting you.</p>
        <p className="mb-6 font-semibold" data-id="element-432">
          The only question left is: Are you ready to try?
        </p>

        <div className="flex flex-col items-center gap-4" data-id="element-433">
          <div className="text-2xl animate-bounce" data-id="element-434">👇</div>
          <a href="https://trynymera.com/products/nymera-lymphatic-drainage-softgels" className="inline-flex items-center justify-center gap-2 bg-[#0d5d55] hover:bg-[#0a4a44] text-white font-bold py-4 px-8 rounded-lg shadow-lg transform transition hover:scale-[1.02] text-lg w-full md:w-auto" data-id="element-435">
            CLAIM YOUR 30% OFF NOW
            <ArrowRight className="w-5 h-5" data-id="element-436" />
          </a>
          <div className="text-2xl animate-bounce" data-id="element-437">👇</div>
        </div>

        <div className="mt-8" data-id="element-438">
          <p className="mt-3 text-sm text-gray-500" data-id="element-439">
            60-Day Money-Back Guarantee
          </p>
          <p className="text-xs text-gray-400 mt-1" data-id="element-440">
            ✓ Guaranteed Safe and Secure Checkout ✓ No-Hassle Returns ✓ Fast
            Shipping
          </p>
        </div>
      </section>
    </div>;
}